import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react'

import { observer } from 'mobx-react'

import cn from 'classnames'
import cloneDeep from 'lodash/cloneDeep'
import uniqueId from 'lodash/uniqueId'

import { UserRole } from 'utils'
import { copyToClipboardOnAndroid } from 'utils/functions'
import { email } from 'utils/regExp'

import { toast } from 'App'
import Button from 'components/Button'
import ButtonIcon from 'components/ButtonIcon'
import Modal from 'components/Modal'

import { useStore } from 'store'

import { ReactComponent as Close } from 'sources/images/close.svg'

import Switch from '../CustomSwitch'
import { BackTwitterUser } from './BackTwitterUser'

import styles from './styles.module.scss'

interface IProps {
  setIsOpen: (value: boolean) => void
  isOpen: boolean
}

interface EmailItem {
  email: string
  id: string
  correct: boolean
}

//Resize
export const InviteModal: FC<IProps> = observer(({ setIsOpen, isOpen }) => {
  const { loading, invite, mediaQuery, profile } = useStore()
  const [inputValue, setInputValue] = useState<string>('')
  const [emailArray, setEmailArray] = useState<EmailItem[]>([])
  const [inviteCode, setInviteCode] = useState<string>('')
  const [inviteCodeLeft, setInviteCodeLeft] = useState<number | null>(null)
  const emailInputRef = useRef<HTMLInputElement | null>(null)
  const [modalType, setModalType] = useState<0 | 1>(0)

  const profileUserRole = profile.userRole || profile.userRoleSession
  const isBlockchainUser = profileUserRole === UserRole.BlockchainUser

  let timerFocusOnEmailInput!: NodeJS.Timeout

  useEffect(() => {
    isOpen && getInviteDate()

    return () => {
      clearTimeout(timerFocusOnEmailInput)
    }
  }, [isOpen])

  const getInviteDate = async () => {
    try {
      loading.setLoading(true)
      const response = await invite.getInviteData()
      setInviteCode(response.InviteCode)
      // setInviteCodeLeft(response.InviteCount)
      setFocusOnEmailInput()
      loading.setLoading(false)
    } catch (e) {
      loading.setLoading(false)
    }
  }

  const setFocusOnEmailInput = () => {
    timerFocusOnEmailInput = setTimeout(() => {
      if (emailInputRef.current) {
        emailInputRef.current.focus()
      }
    }, 100)
  }

  const keyboardEvents = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.persist()
    if (
      event.key === 'Enter' ||
      event.code === 'Enter' ||
      event.key === 'Space' ||
      event.code === 'Space'
    ) {
      if (inputValue.length === 0) return

      const uid = uniqueId()

      if (email(inputValue)) {
        setEmailArray(prev => {
          return prev.concat([{ email: inputValue, id: uid, correct: true }])
        })
      } else {
        setEmailArray(prev => {
          return prev.concat([{ email: inputValue, id: uid, correct: false }])
        })
      }
      setInputValue('')
    }
  }

  const onChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    if (inputValue.length === 0 && Boolean(e.target.value.match(/\s/))) {
      setInputValue('')
    } else {
      setInputValue(e.target.value)
    }
  }

  const onDeleteEmail = (id: string) => {
    setEmailArray(prev => {
      return prev.filter(item => item.id !== id)
    })
    setFocusOnEmailInput()
  }

  const onSendInvite = async () => {
    let newEmailArray = cloneDeep(emailArray)

    if (emailArray.length === 0 && inputValue.length === 0) {
      return toast({
        type: 'warning',
        message: 'You must enter at least one email address',
      })
    } else if (inputValue.length !== 0) {
      if (email(inputValue)) {
        const uid = uniqueId()
        newEmailArray.push({ email: inputValue, id: uid, correct: true })
      } else {
        const uid = uniqueId()
        newEmailArray.push({ email: inputValue, id: uid, correct: false })
      }
    }

    if (newEmailArray.some(item => !email(item.email))) {
      setEmailArray(newEmailArray)
      setInputValue('')
      setFocusOnEmailInput()
      return toast({
        type: 'warning',
        message: 'Email is in wrong format. Please use xxx@xxxxx.com',
      })
    }

    try {
      loading.setLoading(true)
      await invite.sendInviteMessage(newEmailArray.map(item => item.email))
      setIsOpen(false)
      setEmailArray([])
      setInputValue('')
      loading.setLoading(false)
      toast({
        type: 'success',
        message: 'Invitations were successfully sent',
      })
    } catch (e: any) {
      console.log('error', e)
      loading.setLoading(false)
      toast({
        type: 'warning',
        message: e.message || 'Invitations were not sent',
      })
    }
  }

  const copyInviteCode = async (code: string) => {
    try {
      await copyToClipboardOnAndroid(code as string)
      toast({
        type: 'success',
        message: 'The invite code has been copied',
      })
    } catch (err) {
      toast({
        type: 'error',
        message: `Failed to copy`,
      })
    }
  }

  const closeModal = () => {
    setIsOpen(false)
    setEmailArray([])
    setInputValue('')
  }

  const handlerChangeModalType = (value: 0 | 1) => {
    setModalType(value)
  }

  const switchWidth = mediaQuery.minWidth_2200
    ? isBlockchainUser
      ? '240px'
      : '120px'
    : isBlockchainUser
    ? '200px'
    : '100px'

  if (profile?.selectedUser?.DeletionMark) {
    return null
  }

  return (
    <Modal
      boxStyle={{ zIndex: 1102 }}
      onClose={closeModal}
      open={isOpen}
      size={'small'}
      closeButton={true}
      blockOutsideClose
      children={
        <div className={styles.modalContent}>
          <div className={styles.modalTypeSwitch}>
            <Switch
              leftTitle="Invite friends"
              rightTitle="Back X (Twitter) user"
              oneButton={!isBlockchainUser}
              initialPosition={modalType}
              onSwitchClick={handlerChangeModalType}
              switchStyle={{
                width: switchWidth,
                minWidth: switchWidth,
              }}
            />
          </div>
          {modalType === 0 ? (
            <>
              <div className={styles.modalHeader}>
                <div className={styles.title}>Invite friends</div>
                <div className={styles.text}>
                  Invite colleagues and friends to Authencity through Email or
                  by sharing your invite Code and earn AUTH tokens
                </div>
              </div>

              <div
                className={cn({
                  [styles.modalBody]: true,
                  // [styles.displayNone]: Number(inviteCodeLeft) <= 0
                })}
              >
                <div className={styles.inviteDate}>
                  <div className={styles.inviteCodeBox}>
                    Your invite code
                    <div
                      onClick={() => copyInviteCode(inviteCode)}
                      className={styles.inviteCode}
                    >
                      {inviteCode}
                    </div>
                  </div>
                  <ButtonIcon
                    style={{
                      minWidth: mediaQuery.minWidth_2200 ? '28px' : '24px',
                      width: mediaQuery.minWidth_2200 ? '28px' : '24px',
                      height: mediaQuery.minWidth_2200 ? '28px' : '24px',
                    }}
                    styleIcon={{
                      width: mediaQuery.minWidth_2200 ? '13px' : '11px',
                      height: mediaQuery.minWidth_2200 ? '13px' : '11px',
                    }}
                    icon={'info'}
                    tooltip="You can type as many emails as you wish."
                    tooltipPosition="top"
                  />
                  {/*<div className={styles.InviteCount}>{inviteCodeLeft} - invites left</div>*/}
                </div>
                <div
                  onClick={setFocusOnEmailInput}
                  className={styles.modalInputContainer}
                >
                  {emailArray.map(item => (
                    <div
                      className={cn({
                        [styles.emailBlock]: true,
                        [styles.emailBlockError]: !item.correct,
                      })}
                    >
                      {item.email}
                      <Close
                        className={styles.deleteEmailBtn}
                        onClick={() => onDeleteEmail(item.id)}
                      />
                    </div>
                  ))}

                  <div
                    className={cn({
                      [styles.inputWrapper]: true,
                      // [styles.inputWrapperDisplayNone]: Number(inviteCodeLeft) <= 0 || emailArray.length === inviteCodeLeft,
                    })}
                  >
                    <input
                      placeholder={'Email'}
                      className={cn({
                        [styles.emailInput]: true,
                      })}
                      value={inputValue}
                      onChange={onChangeEmail}
                      onKeyPress={e => keyboardEvents(e)}
                      ref={emailInputRef}
                    />
                  </div>
                </div>
                {/*<ButtonIcon*/}
                {/*	style={{ minWidth: '30px', marginTop: '5px' }}*/}
                {/*	styleIcon={{ width: '12px', height: '12px' }}*/}
                {/*	icon={'info'}*/}
                {/*	tooltip='You can type as many emails as you wish.'*/}
                {/*	tooltipPosition='top-start'*/}
                {/*/>*/}
              </div>
              <div
                className={cn({
                  [styles.modalFooter]: true,
                  // [styles.displayNone]: Number(inviteCodeLeft) <= 0
                })}
              >
                <Button
                  onClick={onSendInvite}
                  style={{
                    width: '100%',
                    height: mediaQuery.minWidth_2200 ? '50px' : '40px',
                    borderRadius: 7,
                    background:
                      'linear-gradient(131.69deg, #1f45c3 6.46%, #0e219b 100%)',
                  }}
                  styleText={{ color: '#ffffff', fontWeight: 700 }}
                  btnStyle="classic"
                  text="Send Invite"
                />
              </div>
              <div
                className={cn({
                  [styles.notValidCode]: true,
                  // [styles.notValidCodeVisible]: Number(inviteCodeLeft) <= 0
                })}
              >
                Your invitation code is no longer valid!!!
              </div>
            </>
          ) : (
            <BackTwitterUser setIsOpen={setIsOpen} />
          )}
        </div>
      }
    />
  )
})

InviteModal.displayName = 'InviteModal'
